import type { CardType } from './card.types';

/** Mirror of the backend normalisation: trim + lower-case, local part untouched otherwise. */
export function normalizeCardEmail(value: string): string {
  return value.trim().toLowerCase();
}

function domainLabels(value: string): string[] {
  return value
    .trim()
    .toLowerCase()
    .replace(/^\.+/, '')
    .split('.')
    .filter(label => label.length > 0);
}

/**
 * CARD-002 §3.1: suffix match by domain labels.
 * `.ru` forbids `mail.ru` and `corp.mail.ru`, but not `spb.ru-mail.com` or `ru.com`.
 * A suffix with or without the leading dot is treated the same.
 */
export function isEmailDomainForbidden(email: string, forbidden: readonly string[]): boolean {
  const at = email.lastIndexOf('@');
  if (at < 0) return false;
  const domain = domainLabels(email.slice(at + 1));
  if (domain.length === 0) return false;

  return forbidden.some(suffix => {
    const labels = domainLabels(suffix);
    if (labels.length === 0 || labels.length > domain.length) return false;
    const tail = domain.slice(domain.length - labels.length);
    return tail.every((label, i) => label === labels[i]);
  });
}

export type CardTypeUnavailableReason = 'type-limit' | 'account-limit' | null;

/**
 * CARD-002 §3.3.1: `remainingForUser = min(account remainder, maxPerUser − issued of this type)`
 * and the account remainder is the same for every row. So a zero is the type's own limit
 * whenever some other type still has room, and the account limit when every type is at zero.
 * `null` means the backend did not report a remainder — never disable on that.
 */
export function typeUnavailableReason(
  type: CardType,
  all: readonly CardType[],
): CardTypeUnavailableReason {
  if (type.remainingForUser !== 0) return null;
  const someoneHasRoom = all.some(t => t.remainingForUser !== null && t.remainingForUser > 0);
  return someoneHasRoom ? 'type-limit' : 'account-limit';
}

/** True when the backend reported remainders and every type is exhausted. */
export function allTypesExhausted(types: readonly CardType[]): boolean {
  const known = types.filter(t => t.remainingForUser !== null);
  return known.length > 0 && known.every(t => t.remainingForUser === 0);
}

/** Human-readable product name with a fallback for backends that predate CARD-002. */
export function cardTypeLabel(type: CardType): string {
  return type.displayName.trim() || `${type.currencyCode} · ${type.paymentSystem}`;
}
